import { useMutation, useQuery } from '@tanstack/react-query'

import {
  approveMentor,
  checkMatchedImages,
  cleanupSelfie,
  deleteFaQById,
  deleteTwin,
  fetchImageStyles,
  fetchLinkedInProfile,
  fetchTwinPersonaById,
  getFAQs,
  getKnowledgeBase,
  getKnowledgeFiles,
  getSecurityCode,
  setFaqs,
  setFaqsFiles,
  setImageStyle,
  twinGenerate,
  updateFaQById,
  updateFaQs,
  uploadMedia,
  validateConsent,
  validateImage,
  validateUserConsent,
} from '../../api/twin'
import { removeKnowledgeFile, removeUploadedFile } from '../../api/upload'

export const useUploadMedia = () => useMutation({ mutationFn: uploadMedia })
export const useCleanupSelfie = () => useMutation({ mutationFn: cleanupSelfie })
export const useSetImageStyle = () => useMutation({ mutationFn: setImageStyle })
export const useTwinGenerate = () => useMutation({ mutationFn: twinGenerate })
export const useDeleteTwin = () => useMutation({ mutationFn: deleteTwin })
export const useSetFaqs = () =>
  useMutation({
    mutationFn: setFaqs,
    retry: failureCount => {
      return failureCount < 3
    },
  })
export const useSetFaqsFiles = () =>
  useMutation({
    mutationFn: setFaqsFiles,
    retry: failureCount => {
      return failureCount < 3
    },
  })
export const useCheckMatchedImages = () =>
  useMutation({ mutationFn: checkMatchedImages })
export const useGetKnowledgeBase = () =>
  useMutation({
    mutationFn: getKnowledgeBase,
    retry: failureCount => {
      return failureCount < 3
    },
  })
export const useValidateUserConsent = () =>
  useMutation({ mutationFn: validateUserConsent })

export const useDeleteFaqById = () => useMutation({ mutationFn: deleteFaQById })
export const useUpdateFaqById = () => useMutation({ mutationFn: updateFaQById })
export const useUpdateFaQs = () => useMutation({ mutationFn: updateFaQs })

export const useFetchLinkedInProfile = () =>
  useMutation({
    mutationFn: async ({ url }: { url: string }) => fetchLinkedInProfile(url),
    retry: failureCount => {
      return failureCount < 3
    },
  })

export const useApproveMentor = () => useMutation({ mutationFn: approveMentor })

// Validation hooks (moved from validationHooks.ts)
export const useValidateImage = () => useMutation({ mutationFn: validateImage })

export const useValidateConsent = () =>
  useMutation({ mutationFn: validateConsent })

// twin specific persona fetching with custom polling behavior for twin generation, copied from person as there the check is for ReadyToUse not WaitingForApproval
export const useTwinPersonaById = (
  id: string,
  enabled: boolean = true,
  refetch: boolean = false,
) =>
  useQuery({
    queryKey: ['getTwinPersonaById', id],
    queryFn: () => fetchTwinPersonaById(id),
    enabled: enabled && !!id,
    refetchInterval: ({ state }) => {
      const status = state?.data?.data?.profile?.profile?.status

      // Stop polling when twin reaches final states or WaitingForApproval
      if (
        status === 'ReadyToUse' ||
        status === 'WaitingForApproval' ||
        status === 'Completed' ||
        status === 'Failed' ||
        !refetch
      )
        return false
      return 3000
    },
  })

export const useFetchImageStyles = () =>
  useQuery({
    queryKey: ['fetchImageStyles'],
    queryFn: () => fetchImageStyles(),
  })

export const useRemoveKnowledgeFile = () =>
  useMutation({
    mutationFn: removeKnowledgeFile,
  })
export const useRemoveUploadedFile = () =>
  useMutation({
    mutationFn: removeUploadedFile,
  })

export const useFetchKnowledgeBase = ({ mentorId }: { mentorId: string }) =>
  useQuery({
    queryKey: ['fetchKnowledgeBase', mentorId],
    queryFn: () =>
      getKnowledgeFiles({
        mentorId,
      }),
    enabled: !!mentorId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

export const useFetchFAQ = ({ mentorId }: { mentorId: string }) =>
  useQuery({
    queryKey: ['fetchFAQ', mentorId],
    queryFn: () =>
      getFAQs({
        mentorId,
      }),
    enabled: !!mentorId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

export const useGetSecurityCode = () =>
  useQuery({
    queryKey: ['getSecurityCode'],
    queryFn: () => getSecurityCode(),
    enabled: false,
  })
